<script setup lang="js">
import { onMounted, ref, computed } from 'vue';
import Multiselect from 'vue-multiselect';
import _ from 'lodash';

import { MODAL_SIDE } from '@/const';

import { useModalStore } from '@/stores/modal.store';
import BasicToggleSwitch from '@/components/toggle-switch.vue';
import ModalTemplate from '@/components/newModals/ModalTemplate.vue';
import OutputArgumentSection from './OutputArgumentSection.vue';
import InputArgumentSection from './InputArgumentSection.vue';

const props = defineProps({
  instructionStep: { type: Object },
  availableVars: {
    type: Array,
    required: true
  },
  availableOperations: {
    type: Array,
    required: true
  },
  customVariables: {
    type: Array,
    required: true
  },
  createCustomVar: {
    type: Function,
    required: true
  }
});

const modalStore = useModalStore();

const id = ref();
const operation = ref();
const inputArgVariables = ref({});
const outputArgVariables = ref({});
const conditionEnabled = ref(false);
const condition = ref({
  var: null,
  true_step_id: null,
  false_step_id: null
});

const operationInputArgs = computed(() => operation.value?.input_args || []);
const operationOutputArgs = computed(() => operation.value?.output_args || []);

const allVariables = computed(() => [
  ...props.customVariables,
  ...props.availableVars
]);

const boolVars = computed(() => {
  return allVariables.value.filter((v) => v.type === 'boolean');
});

const formInvalid = computed(() => {
  const requiredFieldEmpty = operationInputArgs.value?.some(
    (arg) => arg.required && !(arg.name in inputArgVariables.value)
  );

  return !operation.value || requiredFieldEmpty;
});

/**
 * Фильтрует аргументы текущей операции и преобразовывает обьект переменной в id
 *
 * @param { Record<string, Object> } varCollection переменные аргументов
 * @param { Array<Object> } operationArgs аргументы операции
 *
 * @returns { Record<string, string> } переменные aргументы в виде строки
 */
function extractVariables(varCollection, operationArgs) {
  const opArgNames = _.map(operationArgs, (arg) => arg.name);

  return _.chain(varCollection)
    .mapValues((varObj) => varObj.name)
    .pickBy((__, argName) => opArgNames.includes(argName))
    .value();
}

function saveData() {
  modalStore.modalEmit('save', {
    id: id.value,
    operation_id: operation.value.id,
    input_vars: extractVariables(
      inputArgVariables.value,
      operationInputArgs.value
    ),
    output_vars: extractVariables(
      outputArgVariables.value,
      operationOutputArgs.value
    ),
    condition: conditionEnabled.value
      ? {
          var: condition.value.var?.name,
          true_step_id: condition.value?.true_step_id,
          false_step_id: condition.value?.false_step_id
        }
      : null,
    children: props.instructionStep?.children || []
  });
}

/**
 * Преобразовывает ключи переменных в обьекты для отображения в multiselect
 *
 * @param { Record<string, string> } varCollection переменные аргументов в виде строки
 * @param { Record<string, Object> } varListObj обьекты переменных
 *
 * @returns { Record<string, Object> } переменные аргументов
 */
function transformIdsToVars(varCollection, varListObj) {
  return _.reduce(
    varCollection,
    (acc, varName, argument) => {
      if (varListObj[varName]) {
        acc[argument] = varListObj[varName];
      }

      return acc;
    },
    {}
  );
}

function updateByProps(step) {
  if (!step) return;

  const allVarObj = _.keyBy(allVariables.value, 'name');
  const customVarObj = _.keyBy(props.customVariables, 'name');

  id.value = step.id;
  operation.value = _.keyBy(props.availableOperations, 'id')[step.operation_id];
  inputArgVariables.value = transformIdsToVars(step.input_vars, allVarObj);
  outputArgVariables.value = transformIdsToVars(step.output_vars, customVarObj);
  conditionEnabled.value = !!(step.condition && step.condition.var);
  condition.value = {
    var: allVarObj[step.condition?.var],
    true_step_id: step.condition?.true_step_id,
    false_step_id: step.condition?.false_step_id
  };
}

onMounted(() => updateByProps(props.instructionStep));
</script>

<template>
  <ModalTemplate :variant="MODAL_SIDE" id="instruction-modal">
    <template #title>
      <span v-if="props.instructionStep">Редактировать операцию</span>
      <span v-else>Создать операцию</span>
    </template>
    <template #default>
      <div class="mt-3">
        <label class="form-label">Операция</label>
        <Multiselect
          v-model="operation"
          :options="props.availableOperations"
          placeholder="Выбрать..."
          :multiple="false"
          label="title"
          track-by="id"
          class="multiselect-style"
        />
      </div>
      <div class="operation-modal_section-header">Входные данные</div>
      <div
        class="mt-3"
        v-for="(inputArg, index) in operationInputArgs"
        :key="index"
      >
        <InputArgumentSection
          v-model="inputArgVariables[inputArg.name]"
          :inputArgument="inputArg"
          :allVariables="allVariables"
        />
      </div>
      <div class="operation-modal_section-header">Выходные данные</div>
      <div
        class="mt-3"
        v-for="(outputArg, index) in operationOutputArgs"
        :key="index"
      >
        <OutputArgumentSection
          v-model="outputArgVariables[outputArg.name]"
          :outputArgument="outputArg"
          :customVariables="props.customVariables"
          :createCustomVar="props.createCustomVar"
        />
      </div>
      <div class="operation-modal_section-header">&nbsp;</div>
      <div class="mt-3 d-flex align-items-center justify-content-between">
        <label class="form-label">Условие</label>
        <basic-toggle-switch v-model="conditionEnabled" />
      </div>
      <div class="mt-3" v-if="conditionEnabled">
        <label class="form-label">Переменная для условия [boolean]</label>

        <Multiselect
          v-model="condition['var']"
          :options="boolVars"
          placeholder="Выбрать..."
          :multiple="false"
          label="title"
          track-by="name"
          class="multiselect-style"
        />
      </div>
      <div
        class="mt-3"
        v-if="
          conditionEnabled &&
          (condition.true_step_id ||
            props.instructionStep?.children?.length > 1)
        "
      >
        <label class="form-label">Шаг при true</label>
        <select v-model="condition.true_step_id" class="form-select">
          <option
            :value="child"
            v-for="child in props.instructionStep?.children"
            :key="child.id"
          >
            {{ child }}
          </option>
        </select>
      </div>
      <div
        class="mt-3"
        v-if="
          conditionEnabled &&
          (condition.false_step_id ||
            props.instructionStep?.children?.length > 1)
        "
      >
        <label class="form-label">Шаг при false</label>
        <select v-model="condition.false_step_id" class="form-select">
          <option
            :value="child"
            v-for="child in props.instructionStep?.children"
            :key="child.id"
          >
            {{ child }}
          </option>
        </select>
      </div>
    </template>
    <template #footer="{ close }">
      <button class="btn-default btn-cancel" @click="close">Отмена</button>
      <button
        class="btn-default btn-primary"
        @click="saveData"
        :disabled="formInvalid"
      >
        Сохранить
      </button>
    </template>
  </ModalTemplate>
</template>
